import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Stepper,
    Step,
    StepLabel,
    Paper,
    Container,
    Typography,
    Alert
} from "@mui/material";
import { v4 as uuidv4 } from 'uuid';
import Swal from 'sweetalert2';
import ConfirmMaterials from "./ConfirmMaterials";
import MaterialForm from "./MaterialForm";
import ProviderStep from "./ProviderStep";
import { SapService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { fechaLocalHoy } from '../../constantes/constants';

export default function MaterialWizard() {
    const { user } = useAuth();
    const [activeStep, setActiveStep] = useState(0);
    const steps = ["Configuración Global", "Detalles de Materiales", "Confirmación Final"];

    // Shared State
    const [sameProvider, setSameProvider] = useState(false);
    const [sameCategoria, setSameCategoria] = useState(false);
    const [sameGrupoDePlanificacion, setSameGrupoDePlanificacion] = useState(false);
    const [sameLineaKUT, setSameLineaKUT] = useState(false);

    const [selectedProvider, setSelectedProvider] = useState('');
    const [selectedCategoria, setSelectedCategoria] = useState('');
    const [selectedGrupoDePlanificacion, setSelectedGrupoDePlanificacion] = useState('');
    const [selectedLineaKUT, setSelectedLineaKUT] = useState('');

    // Los campos obligatorios se marcan en rojo solo después de intentar avanzar
    const [intentoEnvio, setIntentoEnvio] = useState(false);

    const [validador, setValidador] = useState(false); // Used externally? seems local to valid function but kept for consistency

    const generateUUID = () => uuidv4();

    const [materials, setMaterials] = useState([
        {
            fecha: fechaLocalHoy(),
            idregistros: generateUUID(),
            id: 1,
            codigoMaterial: "",
            nombreItem: "Material",
            categoriaProducto: "",
            baseMeasureUnitCode: "",
            lineaKUT: "",
            comentarioLineasDeVenta: "",
            idGrupoPlanificacion: "",
            descripcionMaterial: "",
            proveedor: "",
            grupoAlmacenamiento: "",
            estado: "",
            error: "",
            codigoProveedor: "",
            valoracionProducto: "",
        }
    ]);

    const handleDelete = (id) => {
        // If only one remains, maybe clear it instead of delete? But following original logic:
        if (materials.length === 1) return;
        setMaterials(materials.filter((material) => material.id !== id));
    };

    const validarCampos = () => {
        let todosLlenos = true;
        let campoFaltante = null;

        for (let i = 0; i < materials.length; i++) {
            const m = materials[i];
            for (const key in m) {
                // Skips
                if (["estado", "error", "codigoMaterial", "proveedor", "codigoProveedor"].includes(key)) continue;

                if (m[key] === "" || m[key] === null || m[key] === undefined) {
                    todosLlenos = false;
                    campoFaltante = `${key} (en Material ${m.nombreItem})`;
                    break;
                }
            }

            // El proveedor es obligatorio: sin el ID de SAP el material se crea huérfano
            // y no sincroniza con Logit, lo que bloquea las importaciones (IMP) y las
            // órdenes de compra asociadas.
            if (!m.proveedor) {
                todosLlenos = false;
                campoFaltante = `Proveedor (en Material ${m.nombreItem})`;
            } else {
                // Formato esperado: NOMBRE|SKU|IDSAP
                const parts = m.proveedor.split("|");
                const idSap = parts[2];

                if (!idSap || !idSap.trim() || idSap === "undefined") {
                    todosLlenos = false;
                    campoFaltante = `Proveedor sin ID de SAP (en Material ${m.nombreItem})`;
                } else if (parts[1] === "undefined" || !parts[1]) {
                    todosLlenos = false;
                    campoFaltante = "Proveedor no válido (ID indefinido)";
                }
            }

            // SAP rechaza el vínculo proveedor-material sin número de parte y, con él,
            // el material completo. Solo se reporta si no hay ya otro campo faltante.
            if (todosLlenos && !String(m.codigoProveedor || "").trim()) {
                todosLlenos = false;
                campoFaltante = `Número de Parte (en Material ${m.nombreItem})`;
            }

            if (m.nombreItem && m.nombreItem.length >= 40) {
                todosLlenos = false;
                campoFaltante = "Nombre Item muy largo";
            }

            if (!todosLlenos) break;
        }
        setValidador(todosLlenos);
        return campoFaltante;
    };

    const handleNext = async () => {
        if (activeStep === 1) {
            setIntentoEnvio(true);
            // Validate
            const campoFaltante = validarCampos();

            // Original logic used state `validador` which is set in `validarCampos`.
            // `validarCampos` returns `campoFaltante`.
            // If `campoFaltante` exists, `validador` should be false.

            if (campoFaltante) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Atención',
                    text: `Fata completar o corregir: ${campoFaltante}`,
                    confirmButtonColor: '#0F172A'
                });
                return;
            }

            // Check SAP for duplicates
            let duplicateCount = 0;
            try {
                Swal.fire({ title: 'Validando en SAP...', didOpen: () => Swal.showLoading() });
                const token = await SapService.login();

                const requests = materials.map(async (m) => {
                    if (!m.proveedor) return;
                    // provider format: Tproveedor|idproveedor|Proveedor
                    const parts = m.proveedor.split("|");
                    if (parts.length < 3) return;
                    const idProveedor = parts[1]; // ProductSupplierID? Check original: 
                    // Original: const [, codigoProveedorParte, numeroProveedor] = m.proveedor.split("|");
                    // Original Map: Tproveedor + "|" + idproveedor + "|" + Proveedor
                    // So [0]=Name, [1]=idproveedor(Custom), [2]=Proveedor(InternalID or UUID?)
                    // Original Filter: ProductSupplierID eq '${m.codigoProveedor}' and SupplierID eq '${numeroProveedor}'
                    // Wait, m.codigoProveedor is the user input Part Number? Yes.
                    // SupplierID used is `numeroProveedor` which is index 2 of split.

                    const numeroProveedor = parts[2];

                    if (!m.codigoProveedor || !numeroProveedor) return;

                    const url = `https://my360131.sapbydesign.com/sap/byd/odata/cust/v1/materialesv2/MaterialSupplierInformationCollection?$format=json&$select=ObjectID,InternalID&$filter=ProductSupplierID eq '${m.codigoProveedor}' and SupplierID eq '${numeroProveedor}'`;

                    const response = await SapService.getData(url, token);
                    if (response.d?.results?.length >= 1) {
                        duplicateCount++;
                        Swal.fire({
                            icon: 'warning',
                            title: 'Material Existente',
                            text: `El material ${m.nombreItem} con ese proveedor y parte número ya existe en SAP.`,
                        });
                    }
                });

                await Promise.all(requests);
                Swal.close();

                if (duplicateCount === 0) {
                    setActiveStep((prev) => prev + 1);
                }

            } catch (error) {
                console.error("Validation error", error);
                Swal.fire({ icon: 'error', title: 'Error de Validación', text: 'No se pudo validar con SAP.' });
            }

        } else {
            setActiveStep((prev) => prev + 1);
        }
    };

    const handleBack = () => setActiveStep((prev) => prev - 1);

    const handleConfirm = async () => {
        // Escapa caracteres que rompen el string SQL de BigQuery. Los saltos de línea llegan al
        // pegar textos de varias líneas y, sin escapar, dejan el INSERT en "Unclosed string literal".
        const esc = (val) => String(val ?? '').replace(/\\/g, '\\\\').replace(/"/g, '\\"')
            .replace(/\r/g, '\\r').replace(/\n/g, '\\n').replace(/\t/g, '\\t');

        const usuarioActual = user?.email || '';
        const query = `
        INSERT INTO \`citalsa-1573565243527.Prueba.creacionmateriales\` (
            idregistros, id, codigoMaterial, nombreItem, categoriaProducto,
            baseMeasureUnitCode, lineaKUT, comentarioLineasDeVenta, idGrupoPlanificacion,
            descripcionMaterial, proveedor, grupoAlmacenamiento, estado, error, codigoProveedor, fecha, valoracion, usuario
        ) VALUES
        ${materials.map(m => `(
            "${esc(m.idregistros)}", "${esc(m.id)}", "${esc(m.codigoMaterial)}", "${esc(m.nombreItem)}", "${esc(m.categoriaProducto)}",
            "${esc(m.baseMeasureUnitCode)}", "${esc(m.lineaKUT)}", "${esc(m.comentarioLineasDeVenta)}", "${esc(m.idGrupoPlanificacion)}",
            "${esc(m.descripcionMaterial)}", "${esc(m.proveedor)}", "${esc(m.grupoAlmacenamiento)}", "${esc(m.estado)}", "${esc(m.error)}", "${esc(m.codigoProveedor)}", "${esc(m.fecha)}", "${esc(m.valoracionProducto)}", "${esc(usuarioActual)}"
        )`).join(",\n")}
    `;

        Swal.fire({
            title: 'Guardando...',
            text: 'Enviando datos a BigQuery.',
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading()
        });

        try {
            const data = await SapService.submitMaterials(query);

            if (data.success) {

                const resultNotification = await SapService.notifyAppsScript(materials);

                let successMessage = 'Materiales guardados y notificados correctamente.';

                if (resultNotification && resultNotification.code) {
                    console.log("Material Code Extracted:", resultNotification.code);

                    // Update BigQuery with the new code
                    // Assuming we update the first material or all? 
                    // Since we received one code, and usually create one material, we target the current batch IDs.
                    // Ideally we should match "producto" GUID if possible, but we don't have it mapped.
                    // We will update the materials that were just inserted.

                    const updateQuery = `
                        UPDATE \`citalsa-1573565243527.Prueba.creacionmateriales\` 
                        SET codigoMaterial = "${resultNotification.code}" 
                        WHERE idregistros IN (${materials.map(m => `"${m.idregistros}"`).join(',')})
                    `;

                    try {
                        await SapService.submitMaterials(updateQuery);
                        successMessage += ` Código asignado: ${resultNotification.code}`;
                    } catch (updateError) {
                        console.error("Error updating material code:", updateError);
                        successMessage += " (Error guardando código)";
                    }
                }

                Swal.fire({
                    icon: 'success',
                    title: '¡Éxito!',
                    text: successMessage,
                    confirmButtonColor: '#10B981'
                }).then(() => {
                    window.location.reload();
                });
            } else {
                throw new Error(data.message || "Error desconocido");
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: `Hubo un problema al guardar los datos: ${error.message}`
            });
        }
    };

    // Progress Bar
    // logic: 0 -> 0, 1 -> 50, 2 -> 100
    // const progress = (activeStep / (steps.length - 1)) * 100;

    return (
        <Container maxWidth="lg" sx={{ mt: 5, mb: 10 }}>
            <Paper
                elevation={0}
                sx={{
                    p: 4,
                    mb: 4,
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => window.location.href = '/materiales-creados'}
                    >
                        Ver Materiales Creados
                    </Button>
                </Box>
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Box>
                    {activeStep === 0 && (
                        <ProviderStep
                            sameProvider={sameProvider} setSameProvider={setSameProvider}
                            sameCategoria={sameCategoria} setSameCategoria={setSameCategoria}
                            sameGrupoDePlanificacion={sameGrupoDePlanificacion} setSameGrupoDePlanificacion={setSameGrupoDePlanificacion}
                            sameLineaKUT={sameLineaKUT} setSameLineaKUT={setSameLineaKUT}
                            selectedProvider={selectedProvider} setSelectedProvider={setSelectedProvider}
                            selectedCategoria={selectedCategoria} setSelectedCategoria={setSelectedCategoria}
                            selectedGrupoDePlanificacion={selectedGrupoDePlanificacion} setSelectedGrupoDePlanificacion={setSelectedGrupoDePlanificacion}
                            selectedLineaKUT={selectedLineaKUT} setSelectedLineaKUT={setSelectedLineaKUT}
                            handleNext={handleNext}
                            materials={materials} setMaterials={setMaterials}
                        />
                    )}

                    {activeStep === 1 && (
                        <Box>
                            <MaterialForm
                                materials={materials}
                                setMaterials={setMaterials}
                                selectedCategoria={selectedCategoria}
                                selectedGrupoDePlanificacion={selectedGrupoDePlanificacion}
                                selectedLineaKUT={selectedLineaKUT}
                                mostrarErrores={intentoEnvio}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
                                <Button variant="outlined" onClick={handleBack}>
                                    Atrás
                                </Button>
                                <Button variant="contained" onClick={handleNext}>
                                    Siguiente
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {activeStep === 2 && (
                        <Box>
                            <ConfirmMaterials
                                materials={materials}
                                sameProvider={sameProvider}
                                sameCategoria={sameCategoria}
                                sameGrupoDePlanificacion={sameGrupoDePlanificacion}
                                handleDelete={handleDelete}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
                                <Button variant="outlined" onClick={handleBack}>
                                    Atrás
                                </Button>
                                <Button variant="contained" color="success" onClick={handleConfirm} size="large">
                                    Confirmar y Guardar
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Paper>
        </Container>
    );
}
